import {
    Component,
    Renderer2,
    ElementRef,
    ViewChild,
    EventEmitter,
    Output,
    OnDestroy,
    Input,
    AfterViewInit
} from '@angular/core';
import { ScrollPanel } from 'primeng/scrollpanel';
import { directionsEnum } from '../../../Misc/enums/directions.enum';

@Component({
    selector: 'agpr-scroll-panel',
    templateUrl: './scroll-panel.component.html',
    styleUrls: ['./scroll-panel.component.scss'],
    standalone: false
})
export class ScrollPanelComponent implements OnDestroy, AfterViewInit {
    /** Adds a padding to the scroll area so the "bottom" can be reached to trigger loads before the user actually reaches the bottom */
    @Input() bottomScrollPadding: number = 0;

    @Input() id: string;

    /** Emits every time scroll position hits the bottom */
    justEmittedScrolledToBottom = false;
    @Output() scrolledToBottom: EventEmitter<void> = new EventEmitter<void>();

    /** Emits every time scroll position hits the bottom */
    @Output() scrolledTopChange: EventEmitter<number> = new EventEmitter<number>();

    /** Reference to the internal scroll panel so it can handle the scrolling actions and events */
    @ViewChild('sp', {static: true}) sp: ScrollPanel;

    /** Internal reference to the scroll listener when it has been added */
    protected scrollListener: (e: MouseEvent) => void;

    constructor(
        protected renderer: Renderer2
    ) { }

    /**
     * Scrolls an element into view, preferring the given ScrollPanelComponent (keeps its custom scrollbar in
     * sync) and falling back to the browser's native scrollIntoView when none is given.
     */
    static scrollElementIntoView(
        element: HTMLElement,
        scrollPanel?: ScrollPanelComponent,
        options: ScrollIntoViewOptions = { behavior: 'smooth', block: 'center' }
    ): void {
        if (scrollPanel) {
            scrollPanel.scrollIntoViewNative(element);
        } else {
            element.scrollIntoView(options);
        }
    }

    ngAfterViewInit(): void {
        this.checkForScrollListeners();
    }
    ngOnDestroy(): void {
        if (this.scrollListener) {
            this.removeScrollEvents();
        }
    }

    /** Scrolls an angular reference to an element into view */
    scrollIntoView(el: ElementRef): void {
        const position = this.getScrollPositionOfElement(el.nativeElement as HTMLElement);
        this.scrollTo(position.top, position.left);
    }
    /** Scrolls a DOM element into view*/
    scrollIntoViewNative(el: HTMLElement): void {
        const position = this.getScrollPositionOfElement(el);
        this.scrollTo(position.top, position.left);
    }

    /** Scrolls to the first angular reference to an element to appear in the scroll panel into view */
    scrollFirstIntoView(els: ElementRef[]): void {
        if (!els || els.length < 1) {
            return;
        }
        this.scrollFirstIntoViewNative(els.map(el => el.nativeElement));
    }
    /** Scrolls to the first DOM element to appear in the scroll panel into view */
    scrollFirstIntoViewNative(els: HTMLElement[]): void {
        if (!els || els.length < 1) {
            return;
        }
        const spEl = this.sp.contentViewChild.nativeElement;
        const elementsInsideScrollArea = els.filter(el => spEl.contains(el));
        const positions = elementsInsideScrollArea.map(el => this.getScrollPositionOfElement(el));
        const top = Math.min(...positions.map(pos => pos.top));
        const left = Math.min(...positions.map(pos => pos.left));

        this.scrollTo(top, left);
    }

    /**
     * Scrolls to a specific position in the scroll panel
     * NOTE: default will scroll to top smoothly
     */
    scrollTo(top: number = 0, left: number = 0, smooth: boolean = true): void {
        if (this.sp.contentViewChild.nativeElement.scrollTo) {
            const scrollOptions: ScrollToOptions = {
                top: top,
                left: left
            };
            if (smooth) {
                scrollOptions.behavior = 'smooth';
            }
            this.sp.contentViewChild.nativeElement.scrollTo(scrollOptions);
        } else {
            // Older browsers
            this.sp.contentViewChild.nativeElement.scrollTop = top;
            this.sp.contentViewChild.nativeElement.scrollLeft = left;
        }
        this.sp.refresh();
    }

    /** Gets the position of an element relative to this scroll panel */
    getScrollPositionOfElement(el: HTMLElement): {top: number; left: number} {
        const elRect: ClientRect = el.getBoundingClientRect();
        const spEl: HTMLElement = this.sp.contentViewChild.nativeElement;
        const spRect: ClientRect = spEl.getBoundingClientRect();

        return {
            top: Math.max(0, elRect.top + spEl.scrollTop - spRect.top - spRect.height / 2),
            left: Math.max(0, elRect.left + spEl.scrollLeft - spRect.left - spRect.width / 2)
        };
    }

    /**
     * Adds or removes the scroll DOM event listeners based on whether or not anything is listening for scroll events
     * NOTE: this is for performance reasons when the events are not used, this will not listen to the DOM events
     */
    checkForScrollListeners(): void {
        if (
            this.scrolledToBottom.observed
            || this.scrolledTopChange.observed
        ) {
            if (!this.scrollListener) {
                this.addScrollEvents();
            }
        } else if (this.scrollListener) {
            this.removeScrollEvents();
        }
    }
    /** Adds scroll event listeners */
    protected addScrollEvents(): void {
        this.scrollListener = (e) => this.scrollChanged(e);
        this.sp.contentViewChild.nativeElement.addEventListener('scroll', this.scrollListener);
    }
    /** Removes scroll event listeners */
    protected removeScrollEvents(): void {
        this.sp.contentViewChild.nativeElement.removeEventListener('scroll', this.scrollListener);
        this.scrollListener = null;
    }
    /** Scroll listener that emits `scrolledToBottom` when the user scrolls to the bottom of the scroll panel */
    private scrollChanged(event: MouseEvent): void {

        const target: HTMLElement = event.target as any;

        const totalHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;
        const scrollTop = Math.ceil(target.scrollTop); // sometimes the last pixel is partial, so round up just in case

        if (this.scrolledTopChange.observed) {
            this.scrolledTopChange.emit(scrollTop);
        }

        if (!this.scrolledToBottom.observed) {
            return;
        }

        // Seems to always fire too many times without this
        if (!this.justEmittedScrolledToBottom && scrollTop > 0 && (scrollTop + clientHeight + this.bottomScrollPadding) >= totalHeight) {
            this.justEmittedScrolledToBottom = true;
            setTimeout(() => this.justEmittedScrolledToBottom = false, 1000);
            this.scrolledToBottom.emit();
        }
    }

    public contentDoesVerticalScroll(): boolean {
        const content = this.sp.contentViewChild.nativeElement;
        return content.scrollHeight > content.clientHeight;
    }
    public contentDoesHorizontalScroll(): boolean {
        const content = this.sp.contentViewChild.nativeElement;
        return content.scrollWidth > content.clientWidth;
    }

    public scroll(direction: directionsEnum, pixels: number): void {
        const content = this.sp.contentViewChild.nativeElement;
        switch (direction) {
            case directionsEnum.top:
                this.scrollTo(content.scrollTop - pixels, undefined, true);
                break;
            case directionsEnum.bottom:
                this.scrollTo(content.scrollTop + pixels, undefined, true);
                break;
            case directionsEnum.left:
                this.scrollTo(undefined, content.scrollLeft - pixels, true);
                break;
            case directionsEnum.right:
                this.scrollTo(undefined, content.scrollLeft + pixels, true);
                break;
        }
    }
}
